export function resolveDeckAssetUrl(path, assetsBaseUrl) {
  if (!path) return '';
  const s = String(path);
  if (s.startsWith('http://') || s.startsWith('https://') || s.startsWith('/')) return s;
  const base = assetsBaseUrl ? String(assetsBaseUrl) : '';
  if (!base) return s;
  if (base.endsWith('/')) return `${base}${s}`;
  return `${base}/${s}`;
}

function getVideoUrlFromImageUrl(url) {
  if (!url) return '';
  const s = String(url);
  if (/\.mp4(\?|#|$)/i.test(s)) return s;
  if (/\.(jpe?g|png|webp)(\?|#|$)/i.test(s)) return s.replace(/\.(jpe?g|png|webp)(\?|#|$)/i, '.mp4$2');
  return `${s}.mp4`;
}

function renderBackgroundMediaHtml({ imgSrc, alt, style }) {
  const safeImgSrc = escapeHtmlAttribute(imgSrc);
  const safeAlt = escapeHtmlAttribute(alt || '');
  const safeStyle = escapeHtmlAttribute(style || '');
  const videoSrc = escapeHtmlAttribute(getVideoUrlFromImageUrl(imgSrc));

  if (typeof window !== 'undefined' && window.__predictionsMotionDisabled) {
    return `
    <img src="${safeImgSrc}" alt="${safeAlt}" style="${safeStyle}">
  `;
  }

  return `
    <video
      src="${videoSrc}"
      muted
      autoplay
      loop
      playsinline
      preload="metadata"
      style="${safeStyle}"
      oncanplay="try{var i=this.nextElementSibling;if(i)i.style.display='none';}catch(e){}"
      onerror="try{this.style.display='none';var i=this.nextElementSibling;if(i)i.style.display='block';}catch(e){}"
    ></video>
    <img src="${safeImgSrc}" alt="${safeAlt}" style="${safeStyle}">
  `;
}

function createParagraphs(text) {
  if (!text) return [];
  return String(text)
    .split(/\n\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function getExcerpt(text, maxLen = 140) {
  const t = text == null ? '' : String(text).trim();
  if (!t) return '';
  const firstBlock = t.split(/\n\n+/)[0] || '';
  const compact = String(firstBlock).replace(/\s+/g, ' ').trim();
  if (compact.length <= maxLen) return compact;
  return `${compact.slice(0, Math.max(0, maxLen - 1)).trim()}…`;
}

function escapeHtmlAttribute(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/\s+/g, ' ')
    .trim();
}

function getDeterministicEvidenceId(item) {
  const seed = `${item && item.sector ? item.sector : ''}::${item && item.title ? item.title : ''}`;
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  const n = (h % 999) + 1;
  return String(n).padStart(3, '0');
}

function generateHudOverlay(item) {
  if (!item || item.type !== 'prediction') return '';

  const isMobileView = typeof window !== 'undefined' && window.innerWidth <= 768;
  if (isMobileView) return '';

  const impact = item && typeof item.impact === 'number' ? item.impact : 0.5;
  const riskColor = impact > 0.7 ? '#ff0000' : impact > 0.4 ? '#ffff00' : '#00ff00';
  const riskLabel = item && item.impactType === 'crisis' ? 'CRITICAL' : 'ELEVATED';
  const velocity = Math.round(impact * 50);
  const spread = Math.round(impact * 5);

  const seed = `${item && item.sector ? item.sector : ''}::${item && item.title ? item.title : ''}`;
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  const nodes = 10 + (h % 51);
  const edges = 50 + ((h >>> 8) % 101);

  return `
    <div class="hud-dashboard-scanlines" aria-hidden="true"></div>
    <div class="hud-overlay" data-hud="dashboard">
      <div class="hud-card hud-top-right" style="animation-delay: 0s;">
        <div style="font-size: 0.7rem; color: var(--safety-yellow); margin-bottom: 8px; letter-spacing: 1px;">IMPACT TREND</div>
        <canvas class="dash-canvas" data-kind="impact-trend" width="226" height="100"></canvas>
      </div>

      <div class="hud-card hud-top-left" style="animation-delay: 0.1s;">
        <div style="font-size: 0.7rem; color: var(--safety-yellow); margin-bottom: 5px; letter-spacing: 1px;">RISK LEVEL</div>
        <div style="font-size: 3rem; color: ${riskColor}; text-shadow: 0 0 20px currentColor; line-height: 1; font-weight: bold;">
          ${Math.round(impact * 100)}%
        </div>
        <div style="font-size: 0.6rem; color: rgba(255,255,255,0.6); margin-top: 5px;">${riskLabel}</div>
      </div>

      <div class="hud-card hud-bottom-right" style="animation-delay: 0.2s;">
        <div style="font-size: 0.7rem; color: var(--safety-yellow); margin-bottom: 8px; letter-spacing: 1px;">METRICS</div>
        <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
          <div>
            <div style="font-size: 0.6rem; opacity: 0.6;">VELOCITY</div>
            <div style="font-size: 1.2rem; color: #ffff00;">+${velocity}%</div>
          </div>
          <div>
            <div style="font-size: 0.6rem; opacity: 0.6;">SPREAD</div>
            <div style="font-size: 1.2rem; color: #ffff00;">${spread}s</div>
          </div>
        </div>
        <div style="font-size: 0.6rem; font-family: monospace; color: #00ff00; opacity: 0.8;">NODES: ${nodes} | EDGES: ${edges}</div>
      </div>
    </div>
  `;
}

function renderMatrixRainHtml(item, assetsBaseUrl) {
  return `
    <img src="${resolveDeckAssetUrl(item.img, assetsBaseUrl)}" alt="${item.title}" style="filter: brightness(0.7) contrast(1.2);">
    <canvas class="matrix-canvas" data-kind="matrix-canvas" width="800" height="600" style="mix-blend-mode: screen; opacity: 0.6;"></canvas>
    ${generateHudOverlay(item)}
  `;
}

function renderSectorLandingHtml(item, assetsBaseUrl) {
  const isMobileView = typeof window !== 'undefined' && window.innerWidth <= 768;
  const isTabletView = typeof window !== 'undefined' && window.innerWidth > 768 && window.innerWidth <= 1366 &&
    (window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
  const storyParts = (item && item.story ? String(item.story).split(/\n\n+/) : []).filter(Boolean);
  const storyHtml = storyParts
    .map(
      (p) =>
        `<p style="font-size:${isMobileView ? '0.76rem' : isTabletView ? '0.76rem' : '1.0rem'}; color:#fff; line-height:1.6; margin:0 0 ${
          isMobileView ? '10px' : isTabletView ? '12px' : '14px'
        } 0;">${p}</p>`
    )
    .join('');

  const imgSrc = resolveDeckAssetUrl(item.img, assetsBaseUrl);
  const logoSrc = resolveDeckAssetUrl('images/Obsolete.svg', assetsBaseUrl);

  if (isMobileView) {
    return `
      ${renderBackgroundMediaHtml({
        imgSrc,
        alt: item.title,
        style: 'position:absolute; width:100%; height:100%; object-fit:cover; filter:brightness(0.78) contrast(1.0) blur(2px);'
      })}
      <div style="position:absolute; left:0; right:0; bottom:0; top:var(--pred-mobile-overlay-top); z-index:60; padding:18px 18px 110px 18px; background:linear-gradient(to top, rgba(0,0,0,0.96) 0%, rgba(0,0,0,0.88) 55%, rgba(0,0,0,0.35) 100%); border-top:2px solid var(--safety-yellow); overflow:hidden; box-sizing:border-box;">

        <div style="display:flex; flex-direction:column; gap:14px;">
          <div style="width:220px; height:120px;">
            <iframe src="${logoSrc}" style="width:100%; height:100%; border:none; pointer-events:none; background:transparent;"></iframe>
          </div>
          <div style="font-size:1.1rem; color:var(--safety-yellow); letter-spacing:3px; text-transform:uppercase; font-weight:700; text-shadow:0 0 20px rgba(255,255,0,0.6);">WORK THE FUTURE</div>

          <h2 style="font-size:1.76rem; background:var(--safety-yellow); color:#000; padding:8px 10px; display:inline-block; margin:0; font-weight:900; letter-spacing:1px; line-height:1.1; text-transform:uppercase;">${
            item.sectorDisplayName || item.title
          }</h2>
          <div style="color:var(--safety-yellow); font-size:1.0rem;">━━━━━━━━━━━━━━━━━━━━━━━━</div>

          <div>${storyHtml}</div>

          <div style="margin-top:auto; width:100%;">
            <div style="color:var(--safety-yellow); font-size:1.0rem;">━━━━━━━━━━━━━━━━━━━━━━━━</div>
            <div style="margin-top:10px; font-size:0.95rem; color:rgba(255,255,255,0.9);">
              → Swipe to enter the 5 trajectories
            </div>
          </div>
        </div>
      </div>
    `;
  }

  return `
    ${renderBackgroundMediaHtml({
      imgSrc,
      alt: item.title,
      style: 'position:absolute; width:100%; height:100%; object-fit:cover; filter:brightness(0.78) contrast(1.0) blur(2px);'
    })}
    <div style="position:absolute; top:0; left:0; width:100%; height:100%; display:flex; flex-direction:${
      isMobileView ? 'column' : 'row'
    }; align-items:${isMobileView ? 'stretch' : 'center'}; justify-content:${
      isMobileView ? 'flex-start' : 'center'
    }; z-index:50; padding:${
      isMobileView ? '26px 18px 110px 18px' : isTabletView ? '32px 36px' : '60px 80px'
    }; gap:${
      isMobileView ? '24px' : isTabletView ? '40px' : '80px'
    }; overflow:hidden; background:rgba(0,0,0,0.28); box-sizing:border-box;">

      <div style="flex:${isMobileView ? '0 0 auto' : '1'}; display:flex; flex-direction:column; align-items:flex-start; gap:${
        isMobileView ? '16px' : isTabletView ? '24px' : '40px'
      } ;">
        <div style="width:${isMobileView ? '220px' : isTabletView ? '280px' : '500px'}; height:${
          isMobileView ? '120px' : isTabletView ? '150px' : '300px'
        } ;">
          <iframe src="${logoSrc}" style="width:100%; height:100%; border:none; pointer-events:none; background:transparent;"></iframe>
        </div>
        <div style="font-size:${isMobileView ? '1.1rem' : isTabletView ? '1.45rem' : '2.5rem'}; color:var(--safety-yellow); letter-spacing:${
          isMobileView ? '3px' : isTabletView ? '6px' : '8px'
        }; text-transform:uppercase; font-weight:700; text-shadow:0 0 20px rgba(255,255,0,0.6);">
          WORK THE FUTURE
        </div>
      </div>

      <div style="flex:1; display:flex; flex-direction:column; align-items:flex-start; gap:${
        isMobileView ? '14px' : isTabletView ? '18px' : '26px'
      };">
        <div style="background: rgba(0,0,0,0.78); border: 1px solid rgba(255,255,0,0.35); box-shadow: 0 0 30px rgba(0,0,0,0.55); padding:${
          isMobileView ? '14px 16px' : isTabletView ? '12px 14px' : 'clamp(14px, 1.8vw, 22px) clamp(16px, 2vw, 24px)'
        }; max-width:${isTabletView ? '520px' : 'clamp(380px, 46vw, 740px)'}; display:flex; flex-direction:column; align-items:flex-start; gap:${
          isMobileView ? '14px' : isTabletView ? '12px' : 'clamp(14px, 2.2vw, 26px)'
        };">
          <h2 style="font-size:${isMobileView ? '1.76rem' : isTabletView ? '1.36rem' : 'clamp(1.6rem, 3.12vw, 2.56rem)'}; background:var(--safety-yellow); color:#000; padding:${
            isMobileView ? '8px 10px' : isTabletView ? '6px 10px' : '10px 14px'
          }; display:inline-block; margin:0; font-weight:900; letter-spacing:1px; line-height:1.1; text-transform:uppercase;">
            ${item.sectorDisplayName || item.title}
          </h2>
          <div>${storyHtml}</div>
          <div style="margin-top:${isMobileView ? 'auto' : '0'}; width:100%;">
            <div style="margin-top:${isMobileView ? '10px' : isTabletView ? '8px' : '12px'}; font-size:${
              isMobileView ? '0.76rem' : isTabletView ? '0.72rem' : '0.88rem'
            }; color:rgba(255,255,255,0.9);">
              → ${isMobileView ? 'Swipe' : 'Press right arrow'} to enter the 5 trajectories
            </div>
          </div>
        </div>
      </div>

    </div>
  `;
}

function renderSectorModelHtml(item, assetsBaseUrl) {
  const isMobileView = typeof window !== 'undefined' && window.innerWidth <= 768;
  const isTabletView = typeof window !== 'undefined' && window.innerWidth > 768 && window.innerWidth <= 1366 &&
    (window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
  const storyParts = (item && item.story ? String(item.story).split(/\n\n+/) : []).filter(Boolean);
  const storyHtml = storyParts
    .map(
      (p) =>
        `<p style="font-size:${isMobileView ? '0.76rem' : isTabletView ? '0.74rem' : 'clamp(0.76rem, 1.36vw, 0.92rem)'}; color:#fff; line-height:1.65; margin:0 0 ${
          isMobileView ? '10px' : isTabletView ? '10px' : '12px'
        } 0;">${p}</p>`
    )
    .join('');
  const placeholder = item.img_placeholder ? String(item.img_placeholder) : '';
  const imgSrc = resolveDeckAssetUrl(item.img, assetsBaseUrl);
  const logoSrc = resolveDeckAssetUrl('images/Obsolete.svg', assetsBaseUrl);

  if (isMobileView) {
    return `
      ${renderBackgroundMediaHtml({
        imgSrc,
        alt: item.title,
        style: 'position:absolute; width:100%; height:100%; object-fit:cover; filter:brightness(0.75) contrast(1.0) blur(2px);'
      })}
      <div style="position:absolute; left:0; right:0; bottom:0; top:var(--pred-mobile-overlay-top); z-index:60; padding:18px 18px 110px 18px; background:linear-gradient(to top, rgba(0,0,0,0.96) 0%, rgba(0,0,0,0.88) 55%, rgba(0,0,0,0.35) 100%); border-top:2px solid var(--safety-yellow); overflow:hidden; box-sizing:border-box;">
        <div style="display:flex; flex-direction:column; gap:14px;">
          <div style="width:220px; height:120px;">
            <iframe src="${logoSrc}" style="width:100%; height:100%; border:none; pointer-events:none; background:transparent;"></iframe>
          </div>
          <div style="font-size:1.1rem; color:var(--safety-yellow); letter-spacing:3px; text-transform:uppercase; font-weight:700; text-shadow:0 0 20px rgba(255,255,0,0.6);">WORK THE FUTURE</div>
          <div style="font-size:0.85rem; color:var(--safety-yellow); letter-spacing:2px; text-transform:uppercase; opacity:0.9;">${
            item.sectorDisplayName || item.sector
          }</div>
          <h2 style="font-size:1.76rem; background:var(--safety-yellow); color:#000; padding:8px 10px; display:inline-block; margin:0; font-weight:900; letter-spacing:1px; text-transform:uppercase;">THE MODEL</h2>
          <div>${storyHtml}</div>
          ${
            placeholder
              ? `<div style="margin-top:10px; font-size:0.64rem; color:rgba(255,255,255,0.75);">Image placeholder: <span style="color:var(--safety-yellow);">${placeholder}</span></div>`
              : ''
          }
        </div>
      </div>
    `;
  }

  return `
    ${renderBackgroundMediaHtml({
      imgSrc,
      alt: item.title,
      style: 'position:absolute; width:100%; height:100%; object-fit:cover; filter:brightness(0.75) contrast(1.0) blur(2px);'
    })}
    <div style="position:absolute; inset:0; z-index:50; padding:${
      isMobileView ? '26px 18px 110px 18px' : isTabletView ? '32px 36px' : 'clamp(22px, 4vw, 60px) clamp(20px, 5vw, 80px)'
    }; background:rgba(0,0,0,0.32); overflow:hidden; box-sizing:border-box; display:flex; align-items:${
      isMobileView ? 'stretch' : 'center'
    }; justify-content:${isMobileView ? 'flex-start' : 'center'};">

      <div style="display:flex; flex-direction:${isMobileView ? 'column' : 'row'}; align-items:${
        isMobileView ? 'stretch' : 'center'
      }; gap:${isMobileView ? '24px' : isTabletView ? '40px' : 'clamp(24px, 5vw, 80px)'}; width:100%;">
        <div style="flex:${isMobileView ? '0 0 auto' : '1'}; display:flex; flex-direction:column; align-items:flex-start; gap:${
          isMobileView ? '16px' : isTabletView ? '24px' : '40px'
        };">
          <div style="width:${
            isMobileView ? '220px' : isTabletView ? '280px' : 'clamp(240px, 36vw, 500px)'
          }; height:${isMobileView ? '120px' : isTabletView ? '150px' : 'clamp(140px, 22vw, 300px)'};">
            <iframe src="${logoSrc}" style="width:100%; height:100%; border:none; pointer-events:none; background:transparent;"></iframe>
          </div>
          <div style="font-size:${
            isMobileView ? '1.1rem' : isTabletView ? '1.45rem' : 'clamp(1.35rem, 2.6vw, 2.2rem)'
          }; color:var(--safety-yellow); letter-spacing:${isMobileView ? '3px' : isTabletView ? '6px' : '8px'}; text-transform:uppercase; font-weight:700; text-shadow:0 0 20px rgba(255,255,0,0.6);">
            WORK THE FUTURE
          </div>
        </div>

        <div style="flex:1; display:flex; flex-direction:column; gap:${
          isMobileView ? '14px' : isTabletView ? '18px' : '24px'
        };">
          <div style="background: rgba(0,0,0,0.78); border: 1px solid rgba(255,255,0,0.35); box-shadow: 0 0 30px rgba(0,0,0,0.55); padding:${
            isMobileView ? '14px 16px' : isTabletView ? '12px 14px' : 'clamp(14px, 1.8vw, 22px) clamp(16px, 2vw, 24px)'
          }; max-width:${isTabletView ? '520px' : 'clamp(340px, 42vw, 680px)'}; display: flex; flex-direction: column; gap:${
            isMobileView ? '14px' : isTabletView ? '12px' : 'clamp(14px, 2.2vw, 24px)'
          };">
            <div style="font-size:${isMobileView ? '0.85rem' : isTabletView ? '0.75rem' : '0.9rem'}; color:var(--safety-yellow); letter-spacing:2px; text-transform:uppercase; opacity:0.9;">${
              item.sectorDisplayName || item.sector
            }</div>
            <h2 style="font-size:${isMobileView ? '1.76rem' : isTabletView ? '1.36rem' : 'clamp(1.52rem, 2.88vw, 2.4rem)'}; background:var(--safety-yellow); color:#000; padding:${
              isMobileView ? '8px 10px' : isTabletView ? '6px 10px' : '10px 14px'
            }; display:inline-block; margin:0; font-weight:900; letter-spacing:1px; text-transform:uppercase;">THE MODEL</h2>
            <div>${storyHtml}</div>
            ${
              placeholder
                ? `<div style="margin-top:10px; font-size:${isMobileView ? '0.64rem' : isTabletView ? '0.6rem' : '0.72rem'}; color:rgba(255,255,255,0.75);">Image placeholder: <span style="color:var(--safety-yellow);">${placeholder}</span></div>`
                : ''
            }
          </div>
        </div>
      </div>
    </div>
  `;
}

function renderSectorCtaHtml(item, assetsBaseUrl) {
  const isMobileView = typeof window !== 'undefined' && window.innerWidth <= 768;
  const isTabletView = typeof window !== 'undefined' && window.innerWidth > 768 && window.innerWidth <= 1366 &&
    (window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
  const cta = item && item.cta ? item.cta : {};
  const contact = item && item.contact ? item.contact : 'hello@obsolete.com';
  const reportLink = cta.report_link || '#';
  const consultLink = cta.consultation_link || '#';

  const imgSrc = resolveDeckAssetUrl(item.img, assetsBaseUrl);
  const logoSrc = resolveDeckAssetUrl('images/Obsolete.svg', assetsBaseUrl);

  if (isMobileView) {
    return `
      ${renderBackgroundMediaHtml({
        imgSrc,
        alt: item.title,
        style: 'position:absolute; width:100%; height:100%; object-fit:cover; filter:brightness(0.73) contrast(1.0) blur(2px);'
      })}
      <div style="position:absolute; left:0; right:0; bottom:0; top:var(--pred-mobile-overlay-top); z-index:60; padding:18px 18px 110px 18px; background:linear-gradient(to top, rgba(0,0,0,0.96) 0%, rgba(0,0,0,0.88) 55%, rgba(0,0,0,0.35) 100%); border-top:2px solid var(--safety-yellow); overflow:hidden; box-sizing:border-box;">
        <div style="display:flex; flex-direction:column; gap:14px;">
          <div style="width:220px; height:120px;">
            <iframe src="${logoSrc}" style="width:100%; height:100%; border:none; pointer-events:none; background:transparent;"></iframe>
          </div>
          <div style="font-size:1.1rem; color:var(--safety-yellow); letter-spacing:3px; text-transform:uppercase; font-weight:700; text-shadow:0 0 20px rgba(255,255,0,0.6);">WORK THE FUTURE</div>
          <div style="font-size:0.85rem; color:var(--safety-yellow); letter-spacing:2px; text-transform:uppercase; opacity:0.9;">${
            item.sectorDisplayName || item.sector
          }</div>
          <h2 style="font-size:1.76rem; background:var(--safety-yellow); color:#000; padding:8px 10px; display:inline-block; margin:0; font-weight:900; letter-spacing:1px; text-transform:uppercase;">${
            cta.title || 'NEXT STEPS'
          }</h2>
          <p style="font-size:0.8rem; color:#fff; line-height:1.6; margin:0;">${cta.intro || ''}</p>
          <div style="border-left:3px solid var(--safety-yellow); padding-left:14px; margin-top:8px;">
            <p style="font-size:0.76rem; color:#fff; line-height:1.65; margin:0 0 12px 0;">${
              cta.report_description || ''
            }</p>
            <p style="font-size:0.76rem; color:#fff; line-height:1.65; margin:0;">${
              cta.consultation_description || ''
            }</p>
          </div>
          <div style="display:flex; flex-direction:column; gap:12px; margin-top:16px;">
            <a href="${reportLink}" style="text-decoration:none; background:transparent; color:var(--safety-yellow); border:2px solid var(--safety-yellow); padding:12px 16px; font-family:'Libre Franklin', sans-serif; font-weight:800; letter-spacing:1px; text-transform:uppercase; text-align:center;">View report</a>
            <a href="${consultLink}" style="text-decoration:none; background:var(--safety-yellow); color:#000; border:2px solid var(--safety-yellow); padding:12px 16px; font-family:'Libre Franklin', sans-serif; font-weight:900; letter-spacing:1px; text-transform:uppercase; text-align:center;">Book consultation</a>
          </div>
          <p style="margin-top:12px; font-size:0.76rem; color:rgba(255,255,255,0.9);">${cta.closing || ''}</p>
          <div style="margin-top:10px;">
            <p style="font-size:0.8rem; color:#fff; margin:10px 0 0 0;">${contact}</p>
          </div>
        </div>
      </div>
    `;
  }

  return `
    ${renderBackgroundMediaHtml({
      imgSrc,
      alt: item.title,
      style: 'position:absolute; width:100%; height:100%; object-fit:cover; filter:brightness(0.73) contrast(1.0) blur(2px);'
    })}
    <div style="position:absolute; inset:0; z-index:50; padding:${
      isMobileView ? '26px 18px 110px 18px' : isTabletView ? '32px 36px' : 'clamp(22px, 4vw, 60px) clamp(20px, 5vw, 80px)'
    }; background:rgba(0,0,0,0.34); overflow:hidden; box-sizing:border-box; display:flex; align-items:${
      isMobileView ? 'stretch' : 'center'
    }; justify-content:${isMobileView ? 'flex-start' : 'center'};">

      <div style="display:flex; flex-direction:${isMobileView ? 'column' : 'row'}; align-items:${
        isMobileView ? 'stretch' : 'center'
      }; gap:${isMobileView ? '24px' : isTabletView ? '40px' : 'clamp(24px, 5vw, 80px)'}; width:100%;">
        <div style="flex:${isMobileView ? '0 0 auto' : '1'}; display:flex; flex-direction:column; align-items:flex-start; gap:${
          isMobileView ? '16px' : isTabletView ? '24px' : '40px'
        };">
          <div style="width:${
            isMobileView ? '220px' : isTabletView ? '280px' : 'clamp(240px, 36vw, 500px)'
          }; height:${isMobileView ? '120px' : isTabletView ? '150px' : 'clamp(140px, 22vw, 300px)'};">
            <iframe src="${logoSrc}" style="width:100%; height:100%; border:none; pointer-events:none; background:transparent;"></iframe>
          </div>
          <div style="font-size:${
            isMobileView ? '1.1rem' : isTabletView ? '1.45rem' : 'clamp(1.35rem, 2.6vw, 2.2rem)'
          }; color:var(--safety-yellow); letter-spacing:${isMobileView ? '3px' : isTabletView ? '6px' : '8px'}; text-transform:uppercase; font-weight:700; text-shadow:0 0 20px rgba(255,255,0,0.6);">
            WORK THE FUTURE
          </div>
        </div>

        <div style="flex:1; display:flex; flex-direction:column; gap:${
          isMobileView ? '14px' : isTabletView ? '16px' : '18px'
        };">
          <div style="background: rgba(0,0,0,0.78); border: 1px solid rgba(255,255,0,0.35); box-shadow: 0 0 30px rgba(0,0,0,0.55); padding:${
            isMobileView ? '14px 16px' : isTabletView ? '12px 14px' : 'clamp(14px, 1.8vw, 22px) clamp(16px, 2vw, 24px)'
          }; max-width:${isTabletView ? '520px' : 'clamp(340px, 42vw, 680px)'}; display: flex; flex-direction: column; gap:${
            isMobileView ? '14px' : isTabletView ? '12px' : 'clamp(14px, 2.2vw, 18px)'
          };">
            <div style="font-size:${isMobileView ? '0.85rem' : isTabletView ? '0.75rem' : '0.9rem'}; color:var(--safety-yellow); letter-spacing:2px; text-transform:uppercase; opacity:0.9;">${
              item.sectorDisplayName || item.sector
            }</div>
            <h2 style="font-size:${isMobileView ? '1.76rem' : isTabletView ? '1.36rem' : 'clamp(1.52rem, 2.88vw, 2.4rem)'}; background:var(--safety-yellow); color:#000; padding:${
              isMobileView ? '8px 10px' : isTabletView ? '6px 10px' : '10px 14px'
            }; display:inline-block; margin:0; font-weight:900; letter-spacing:1px; text-transform:uppercase;">${
              cta.title || 'NEXT STEPS'
            }</h2>
            <p style="font-size:${isMobileView ? '0.8rem' : isTabletView ? '0.74rem' : 'clamp(0.8rem, 1.6vw, 0.96rem)'}; color:#fff; line-height:1.6; margin:0;">${cta.intro || ''}</p>
            <div style="border-left:3px solid var(--safety-yellow); padding-left:14px; margin-top:8px;">
              <p style="font-size:${isMobileView ? '0.76rem' : isTabletView ? '0.66rem' : 'clamp(0.72rem, 1.32vw, 0.84rem)'}; color:#fff; line-height:1.65; margin:0 0 12px 0;">${
                cta.report_description || ''
              }</p>
              <p style="font-size:${isMobileView ? '0.76rem' : isTabletView ? '0.66rem' : 'clamp(0.72rem, 1.32vw, 0.84rem)'}; color:#fff; line-height:1.65; margin:0;">${
                cta.consultation_description || ''
              }</p>
            </div>
            <div style="display:flex; flex-direction:${isMobileView ? 'column' : 'row'}; gap:12px; margin-top:16px;">
              <a href="${reportLink}" style="text-decoration:none; background:transparent; color:var(--safety-yellow); border:2px solid var(--safety-yellow); padding:12px 16px; font-family:'Libre Franklin', sans-serif; font-weight:800; letter-spacing:1px; text-transform:uppercase; text-align:center;">View report</a>
              <a href="${consultLink}" style="text-decoration:none; background:var(--safety-yellow); color:#000; border:2px solid var(--safety-yellow); padding:12px 16px; font-family:'Libre Franklin', sans-serif; font-weight:900; letter-spacing:1px; text-transform:uppercase; text-align:center;">Book consultation</a>
            </div>
            <p style="margin-top:12px; font-size:${isMobileView ? '0.76rem' : isTabletView ? '0.66rem' : 'clamp(0.72rem, 1.32vw, 0.84rem)'}; color:rgba(255,255,255,0.9);">${cta.closing || ''}</p>
            <div style="margin-top:${isMobileView ? 'auto' : '0'};">
              <p style="font-size:${isMobileView ? '0.8rem' : isTabletView ? '0.74rem' : 'clamp(0.8rem, 1.6vw, 0.96rem)'}; color:#fff; margin:10px 0 0 0;">${contact}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

function renderGlobalTitleHtml(item, assetsBaseUrl) {
  const isMobileView = typeof window !== 'undefined' && window.innerWidth <= 768;
  const isTabletView = typeof window !== 'undefined' && window.innerWidth > 768 && window.innerWidth <= 1366 &&
    (window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
  const imgSrc = resolveDeckAssetUrl(item.img, assetsBaseUrl);
  const logoSrc = resolveDeckAssetUrl('images/Obsolete.svg', assetsBaseUrl);

  return `
    ${renderBackgroundMediaHtml({
      imgSrc,
      alt: item.title,
      style: 'position: absolute; width: 100%; height: 100%; object-fit: cover; filter: brightness(0.82);'
    })}
    ${
      isMobileView
        ? `
    <div style="position:absolute; left:0; right:0; bottom:0; top:var(--pred-mobile-overlay-top); z-index:60; padding:18px 18px 110px 18px; background:linear-gradient(to top, rgba(0,0,0,0.96) 0%, rgba(0,0,0,0.88) 55%, rgba(0,0,0,0.35) 100%); border-top:2px solid var(--safety-yellow); overflow:hidden; box-sizing:border-box;">
      <div style="display:flex; flex-direction:column; gap:14px;">
        <div style="width:220px; height:120px;">
          <iframe src="${logoSrc}" style="width:100%; height:100%; border:none; pointer-events:none; background:transparent;"></iframe>
        </div>
        <div style="font-size:1.1rem; color:var(--safety-yellow); letter-spacing:3px; text-transform:uppercase; font-weight:700; text-shadow:0 0 20px rgba(255,255,0,0.6);">WORK THE FUTURE</div>
        <h2 style="font-size:1.76rem; color:var(--safety-yellow); margin:0; font-weight:900; letter-spacing:2px; line-height:1.2;">2029 CREATIVE<br>INDUSTRIES</h2>
        <p style="font-size:0.8rem; color:#fff; line-height:1.5; margin:0;">Foresight provides 48-month workforce planning intelligence for the UK creative industries.</p>
        <p style="font-size:0.76rem; color:#fff; line-height:1.6; margin:0;">
          <span style="color: var(--safety-yellow); font-weight: bold;">687 nodes.</span><br>
          <span style="color: var(--safety-yellow); font-weight: bold;">1944 edges.</span><br>
          <span style="color: var(--safety-yellow); font-weight: bold;">76% accuracy.</span>
        </p>
        <p style="font-size:0.76rem; color:#fff; margin:0;">Sectors covered: Screen, Music, Games, Performing Arts, Publishing, Advertising, Design/UX + cross-sector infrastructure.</p>
        <p style="font-size:0.96rem; color:var(--safety-yellow); margin:0; font-weight:900; text-shadow:0 0 30px rgba(255,255,0,0.8); letter-spacing:1px;">Signals early. Supports action.</p>
        <p style="font-size:0.8rem; color:#fff; margin:0;">hello@obsolete.com</p>
      </div>
    </div>
    `
        : `
    <div class="pred-fullbleed-title-layout" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; display: flex; align-items: center; justify-content: space-between; z-index: 50; padding: ${
      isTabletView ? '28px 36px' : 'clamp(22px, 4vw, 60px) clamp(20px, 5vw, 80px)'
    }; gap: ${isTabletView ? '36px' : 'clamp(24px, 5vw, 80px)'}; overflow:hidden; box-sizing:border-box;">
      <div style="flex: 1; display: flex; flex-direction: column; align-items: flex-start; gap: ${
        isTabletView ? '24px' : 'clamp(18px, 3vw, 40px)'
      };">
        <div style="width: ${isTabletView ? '300px' : 'clamp(240px, 36vw, 500px)'}; height: ${
          isTabletView ? '170px' : 'clamp(140px, 22vw, 300px)'
        };"><iframe src="${logoSrc}" style="width: 100%; height: 100%; border: none; pointer-events: none; background: transparent;"></iframe></div>
        <div style="font-size: ${
          isTabletView ? '1.45rem' : 'clamp(1.4rem, 2.6vw, 2.5rem)'
        }; color: var(--safety-yellow); letter-spacing: ${isTabletView ? '6px' : '8px'}; text-transform: uppercase; font-weight: 700; text-shadow: 0 0 20px rgba(255,255,0,0.6);">WORK THE FUTURE</div>
      </div>
      <div style="flex: 1; display: flex; flex-direction: column; align-items: flex-start;">
        <div class="pred-fullbleed-title-plate" style="background: rgba(0,0,0,0.78); border: 1px solid rgba(255,255,0,0.35); box-shadow: 0 0 30px rgba(0,0,0,0.55); padding: ${
          isTabletView ? '12px 14px' : 'clamp(14px, 1.8vw, 22px) clamp(16px, 2vw, 24px)'
        }; max-width: ${isTabletView ? '520px' : 'clamp(340px, 42vw, 680px)'}; display: flex; flex-direction: column; align-items: flex-start; gap: ${
          isTabletView ? '12px' : 'clamp(14px, 2.2vw, 30px)'
        };"><h2 style="font-size: ${
          isTabletView ? '1.36rem' : 'clamp(1.6rem, 3.04vw, 2.4rem)'
        }; color: var(--safety-yellow); margin: 0; font-weight: 900; letter-spacing: 2px; line-height: 1.2;">2029 CREATIVE<br>INDUSTRIES</h2>
        <p style="font-size: ${
          isTabletView ? '0.74rem' : 'clamp(0.84rem, 1.76vw, 1.44rem)'
        }; color: #fff; line-height: 1.5; margin: 0;">Foresight provides 48-month<br>workforce planning intelligence<br>for the UK creative industries.</p>
        <p style="font-size: ${
          isTabletView ? '0.66rem' : 'clamp(0.76rem, 1.52vw, 1.28rem)'
        }; color: #fff; line-height: 1.6; margin: 0;">
          <span style="color: var(--safety-yellow); font-weight: bold;">687 nodes.</span><br>
          <span style="color: var(--safety-yellow); font-weight: bold;">1944 edges.</span><br>
          <span style="color: var(--safety-yellow); font-weight: bold;">76% accuracy.</span>
        </p>
        <p style="font-size: ${
          isTabletView ? '0.62rem' : 'clamp(0.72rem, 1.24vw, 1rem)'
        }; color: #fff; margin: 0;">Sectors covered: Screen, Music, Games, Performing Arts, Publishing, Advertising, Design/UX + cross-sector infrastructure.</p>
        <p style="font-size: ${
          isTabletView ? '0.72rem' : 'clamp(0.88rem, 1.92vw, 1.6rem)'
        }; color: var(--safety-yellow); margin: 0; font-weight: 900; text-shadow: 0 0 30px rgba(255,255,0,0.8); letter-spacing: 1px;">Signals early. Supports action.</p>
        <p style="font-size: ${
          isTabletView ? '0.7rem' : 'clamp(0.76rem, 1.4vw, 1.12rem)'
        }; color: #fff; margin: 0;">hello@obsolete.com</p>
        </div>
      </div>
    </div>
    `
    }
  `;
}

function renderComparisonHtml(item, assetsBaseUrl) {
  const src = resolveDeckAssetUrl(item.img, assetsBaseUrl);
  const baseline = getExcerpt(item && item.story, 140);
  const mitigated = getExcerpt(item && item.mitigated_story, 140);
  const hasMitigated = !!mitigated;
  return `
    <div class="split-image split-image-left">
      <img src="${src}" alt="${item.title}" style="filter: grayscale(1) brightness(0.7);">
      <div class="semantic-card semantic-left">
        <div class="semantic-kicker">BASELINE</div>
        <div class="semantic-body">${baseline}</div>
      </div>
    </div>

    <div class="split-image split-image-right">
      <img src="${src}" alt="${item.title}" style="filter: brightness(0.9) contrast(1.2) saturate(1.25);">
      <div class="semantic-card semantic-right">
        <div class="semantic-kicker">${hasMitigated ? 'MITIGATED' : 'BASELINE'}</div>
        <div class="semantic-body">${hasMitigated ? mitigated : baseline}</div>
      </div>
    </div>

    <div class="split-divider">
      <div class="split-divider-label">BASELINE / MITIGATED</div>
    </div>
    ${generateHudOverlay(item)}
  `;
}

function renderTimelineHtml(item, assetsBaseUrl) {
  const baseline = getExcerpt(item && item.story, 70);
  const mitigated = getExcerpt(item && item.mitigated_story, 70);
  const hasMitigated = !!mitigated;
  return `
    <img src="${resolveDeckAssetUrl(item.img, assetsBaseUrl)}" alt="${item.title}" style="filter: brightness(0.65) contrast(1.05);">
    
    <div class="timeline-track baseline-track">
      <div class="timeline-line">
        <div class="timeline-progress"></div>
      </div>
      <div class="timeline-node" style="left: 8%;"></div>
      <div class="timeline-node" style="left: 33%;"></div>
      <div class="timeline-node crisis" style="left: 60%;"></div>
      <div class="timeline-node" style="left: 88%; opacity: 0.35;"></div>
      <div class="timeline-tag">BASELINE</div>
      <div class="timeline-excerpt">${baseline}</div>
    </div>

    ${
      hasMitigated
        ? `
    <div class="timeline-track mitigated-track">
      <div class="timeline-line">
        <div class="timeline-progress mitigated"></div>
      </div>
      <div class="timeline-node mitigated" style="left: 8%;"></div>
      <div class="timeline-node mitigated" style="left: 33%;"></div>
      <div class="timeline-node mitigated" style="left: 60%;"></div>
      <div class="timeline-node mitigated" style="left: 88%;"></div>
      <div class="timeline-tag">MITIGATED</div>
      <div class="timeline-excerpt">${mitigated}</div>
    </div>
    `
        : ''
    }

    <div class="timeline-years">
      <span>NOW</span>
      <span>+12M</span>
      <span>+24M</span>
      <span>+36M</span>
    </div>
    ${generateHudOverlay(item)}
  `;
}

function renderFullBleedHtml(item, assetsBaseUrl) {
  return `
    ${renderBackgroundMediaHtml({
      imgSrc: resolveDeckAssetUrl(item.img, assetsBaseUrl),
      alt: item.title,
      style: 'position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover;'
    })}
    ${generateHudOverlay(item)}
  `;
}

function renderGridHtml(item, assetsBaseUrl) {
  const src = resolveDeckAssetUrl(item.img, assetsBaseUrl);
  const baseline = getExcerpt(item && item.story, 90);
  const mitigated = getExcerpt(item && item.mitigated_story, 90);
  const hasMitigated = !!mitigated;
  const impact = item && typeof item.impact === 'number' ? item.impact : 0.5;
  const threat = Math.round(impact * 100);
  const velocity = Math.round(impact * 50);
  const spread = Math.round(impact * 5);
  return `
    <div class="grid-cell" data-variant="baseline">
      <img src="${src}" alt="${item.title}" style="filter: grayscale(1) contrast(1.2) brightness(0.8);">
      <div class="grid-label">BASELINE</div>
      <div class="grid-snippet">${baseline}</div>
    </div>
    <div class="grid-cell" data-variant="mitigated">
      <img src="${src}" alt="${item.title}" style="filter: contrast(1.2) saturate(1.25) brightness(0.95);">
      <div class="grid-label">${hasMitigated ? 'MITIGATED' : 'NO MITIGATION'}</div>
      <div class="grid-snippet">${hasMitigated ? mitigated : ''}</div>
    </div>
    <div class="grid-cell" data-variant="metrics">
      <img src="${src}" alt="${item.title}" style="filter: grayscale(1) brightness(0.45) contrast(1.4);">
      <div class="grid-label">RISK</div>
      <div class="grid-metrics">
        <div><span>THREAT</span><b>${threat}%</b></div>
        <div><span>VELOCITY</span><b>+${velocity}%</b></div>
        <div><span>SPREAD</span><b>${spread}s</b></div>
      </div>
    </div>
    <div class="grid-cell" data-variant="deltas">
      <img src="${src}" alt="${item.title}" style="filter: hue-rotate(90deg) brightness(0.55) contrast(1.5);">
      <div class="grid-label">DELTA</div>
      <div class="grid-snippet">${hasMitigated ? 'Mitigation path available. Toggle mitigation to compare text plate.' : 'No mitigated story available for this slide.'}</div>
    </div>
    ${generateHudOverlay(item)}
  `;
}

function renderTerminalHtml(item, assetsBaseUrl) {
  const baseline = getExcerpt(item && item.story, 120);
  const mitigated = getExcerpt(item && item.mitigated_story, 120);
  const hasMitigated = !!mitigated;
  const impact = item && typeof item.impact === 'number' ? item.impact : 0.5;
  const threat = Math.round(impact * 100);

  const baselineAttr = escapeHtmlAttribute(`> BASELINE: ${baseline}`);
  const mitigatedAttr = escapeHtmlAttribute(`> MITIGATED: ${mitigated}`);
  return `
    <img src="${resolveDeckAssetUrl(item.img, assetsBaseUrl)}" alt="${item.title}" style="filter: brightness(0.55) contrast(1.15) saturate(0.9) hue-rotate(-20deg);">
    <div class="terminal-overlay" style="position: absolute; top: 20px; left: 20px; right: 20px; bottom: 20px; color: #00ff00; font-family: 'Courier New', monospace; font-size: 0.95rem; text-shadow: 0 0 6px rgba(0,255,0,0.6);">
      <div data-terminal-type="> SYSTEM READY" data-type-speed="22" data-type-delay="0"></div>
      <div data-terminal-type="> THREAT LEVEL: ${threat}%" data-type-speed="18" data-type-delay="450"></div>
      <div data-terminal-type="${baselineAttr}" data-type-speed="12" data-type-delay="900"></div>
      ${hasMitigated ? `<div data-terminal-type="${mitigatedAttr}" data-type-speed="12" data-type-delay="1900"></div>` : ''}
      <div data-terminal-type="> MONITORING…" data-type-speed="18" data-type-delay="2900"></div>
      <div class="terminal-cursor">█</div>
    </div>
    <div class="terminal-scanlines" aria-hidden="true"></div>
    ${generateHudOverlay(item)}
  `;
}

function renderGlitchArtHtml(item, assetsBaseUrl) {
  const src = resolveDeckAssetUrl(item.img, assetsBaseUrl);
  return `
    <img src="${src}" alt="${item.title}" class="glitch-base" style="filter: brightness(0.85) contrast(1.15);">
    <img src="${src}" alt="" class="glitch-layer glitch-layer-a" aria-hidden="true">
    <img src="${src}" alt="" class="glitch-layer glitch-layer-b" aria-hidden="true">
    <div class="glitch-scanlines" aria-hidden="true"></div>
    <div class="glitch-burst" aria-hidden="true"></div>
    ${generateHudOverlay(item)}
  `;
}

function renderHologramHtml(item, assetsBaseUrl) {
  return `
    <img src="${resolveDeckAssetUrl(item.img, assetsBaseUrl)}" alt="${item.title}">
    <div class="hologram-interference" aria-hidden="true"></div>
    <div class="hologram-edgeglow" aria-hidden="true"></div>
    ${generateHudOverlay(item)}
  `;
}

function renderExplosiveHtml(item, assetsBaseUrl) {
  const fragments = 9;
  let html = '';

  const src = resolveDeckAssetUrl(item.img, assetsBaseUrl);

  for (let i = 0; i < fragments; i++) {
    const row = Math.floor(i / 3);
    const col = i % 3;
    const tx = (col - 1) * -300 + (Math.random() - 0.5) * 200;
    const ty = (row - 1) * -300 + (Math.random() - 0.5) * 200;
    const rot = (Math.random() - 0.5) * 720;

    html += `
      <div class="fragment" style="
        position: absolute;
        left: ${col * 33.33}%;
        top: ${row * 33.33}%;
        width: 33.33%;
        height: 33.33%;
        overflow: hidden;
        z-index: 2;
        --tx: ${tx}px;
        --ty: ${ty}px;
        --rot: ${rot}deg;
      ">
        <img src="${src}" alt="${item.title}" style="
          position: absolute;
          left: ${-col * 100}%;
          top: ${-row * 100}%;
          width: 300%;
          height: 300%;
          object-fit: cover;
        ">
      </div>
    `;
  }

  return html + generateHudOverlay(item);
}

function renderKaleidoscopeHtml(item, assetsBaseUrl) {
  const src = resolveDeckAssetUrl(item.img, assetsBaseUrl);
  return `
    <img src="${src}" alt="${item.title}">
    <div class="kaleidoscope-layer" style="background-image: url('${src}'); background-size: cover; opacity: 0.3;"></div>
    <div class="kaleidoscope-layer" style="background-image: url('${src}'); background-size: cover; opacity: 0.2; animation-delay: -5s;"></div>
    ${generateHudOverlay(item)}
  `;
}

function renderVortexHtml(item, assetsBaseUrl) {
  return `
    <img src="${resolveDeckAssetUrl(item.img, assetsBaseUrl)}" alt="${item.title}">
    <div class="vortex-vignette" aria-hidden="true"></div>
    ${generateHudOverlay(item)}
  `;
}

function renderNeonCityHtml(item, assetsBaseUrl) {
  const src = resolveDeckAssetUrl(item.img, assetsBaseUrl);
  return `
    <img src="${src}" alt="${item.title}" style="filter: brightness(0.6) contrast(1.3) saturate(1.5);">
    <div class="neon-glow" aria-hidden="true"></div>
    <div class="neon-lines" aria-hidden="true"></div>
    <div class="neon-shape neon-shape-a" aria-hidden="true"></div>
    <div class="neon-shape neon-shape-b" aria-hidden="true"></div>
    ${generateHudOverlay(item)}
  `;
}

function renderCosmicHtml(item, assetsBaseUrl) {
  return `
    <img src="${resolveDeckAssetUrl(item.img, assetsBaseUrl)}" alt="${item.title}" style="filter: brightness(0.8) saturate(1.3);">
    <canvas class="cosmic-canvas" data-kind="cosmic-canvas" width="800" height="600" style="mix-blend-mode: screen; opacity: 0.5;"></canvas>
    ${generateHudOverlay(item)}
  `;
}

function renderShockwaveHtml(item, assetsBaseUrl) {
  return `
    <img src="${resolveDeckAssetUrl(item.img, assetsBaseUrl)}" alt="${item.title}">
    <div class="shockwave-ring" style="animation-delay: 0s;"></div>
    <div class="shockwave-ring" style="animation-delay: 1s;"></div>
    <div class="shockwave-ring" style="animation-delay: 2s;"></div>
    ${generateHudOverlay(item)}
  `;
}

function renderFactoryStampHtml(item, assetsBaseUrl) {
  const src = resolveDeckAssetUrl(item.img, assetsBaseUrl);
  return `
    <div class="overscan">
      <div class="stamp-base"></div>
      <div class="stamp-press">
        <img src="${src}" alt="${item.title}">
      </div>
      <div class="stamp-impression">
        <img src="${src}" alt="${item.title}">
      </div>
    </div>
    ${generateHudOverlay(item)}
  `;
}

function renderPropagandaDropHtml(item, assetsBaseUrl) {
  const src = resolveDeckAssetUrl(item.img, assetsBaseUrl);
  return `
    <div class="leaflet-wrapper">
      <img src="${src}" alt="${item.title}" style="width: 100%; height: 100%; object-fit: contain; filter: brightness(0.95) contrast(1.1); box-shadow: 0 20px 60px rgba(0,0,0,0.6);">
      <div style="position: absolute; inset: 0; background: linear-gradient(135deg, transparent 0%, rgba(255,255,0,0.05) 50%, transparent 100%); pointer-events: none;"></div>
    </div>
    <div class="thumbtack top-left"></div>
    <div class="thumbtack top-right"></div>
    <div class="thumbtack bottom-left"></div>
    <div class="thumbtack bottom-right"></div>
    ${generateHudOverlay(item)}
  `;
}

function renderBroadcastInterruptHtml(item, assetsBaseUrl) {
  const src = resolveDeckAssetUrl(item.img, assetsBaseUrl);
  return `
    <div class="broadcast-bars" aria-hidden="true"></div>
    <div class="broadcast-static" aria-hidden="true"></div>
    <div class="broadcast-content">
      <img src="${src}" alt="${item.title}">
    </div>
    <div class="broadcast-header">
      <span class="blink">●</span> EMERGENCY BROADCAST SYSTEM
    </div>
    <canvas class="scanline-canvas" data-kind="scanline-canvas" width="800" height="600" aria-hidden="true"></canvas>
    ${generateHudOverlay(item)}
  `;
}

function renderWheatPasteHtml(item, assetsBaseUrl) {
  const src = resolveDeckAssetUrl(item.img, assetsBaseUrl);
  return `
    <div style="position: absolute; inset: 0; background: linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%);"></div>
    <div class="paper-grain" aria-hidden="true"></div>
    <div class="poster-crumpled">
      <img src="${src}" alt="${item.title}" style="width: 100%; height: 100%; object-fit: contain; filter: brightness(0.9) contrast(1.1) sepia(0.1); box-shadow: 0 10px 40px rgba(0,0,0,0.5);">
      <div style="position: absolute; inset: 0; background: linear-gradient(45deg, transparent 0%, rgba(255,255,255,0.05) 50%, transparent 100%); pointer-events: none;"></div>
    </div>
    <div class="paste-glue" aria-hidden="true"></div>
    <div class="paste-drips"></div>
    <canvas class="paste-brush-canvas" data-kind="paste-brush-canvas" width="800" height="600" style="position: absolute; inset: 0; pointer-events: none; opacity: 0.2;"></canvas>
    ${generateHudOverlay(item)}
  `;
}

function renderPrintPressHtml(item, assetsBaseUrl) {
  const src = resolveDeckAssetUrl(item.img, assetsBaseUrl);
  return `
    <div style="position: absolute; inset: 0; background: #f5f5dc;"></div>
    <div class="print-layer yellow-layer" style="position: absolute; inset: 0; animation: printPress 1.5s ease-out 0s forwards;">
      <img src="${src}" alt="${item.title}" style="width: 100%; height: 100%; object-fit: cover; filter: brightness(1.1) contrast(0.9); mix-blend-mode: multiply; opacity: 0.7;">
    </div>
    <div class="print-layer black-layer" style="position: absolute; inset: 0; animation: printPress 1.5s ease-out 0.8s forwards;">
      <img src="${src}" alt="${item.title}" style="width: 100%; height: 100%; object-fit: cover; filter: contrast(1.3) brightness(0.7); mix-blend-mode: multiply;">
    </div>
    <div class="ink-splatter" style="position: absolute; top: 10%; right: 15%; width: 80px; height: 80px; background: radial-gradient(circle, rgba(0,0,0,0.3) 0%, transparent 70%); filter: blur(3px); animation: inkAppear 0.5s ease-out 2s both;"></div>
    <div class="ink-splatter" style="position: absolute; bottom: 20%; left: 10%; width: 60px; height: 60px; background: radial-gradient(circle, rgba(0,0,0,0.2) 0%, transparent 70%); filter: blur(2px); animation: inkAppear 0.5s ease-out 2.2s both;"></div>
    <div class="registration-marks" style="position: absolute; inset: 0; pointer-events: none;">
      <div class="reg-mark top-left" style="position: absolute; top: 10px; left: 10px; width: 20px; height: 20px; border: 2px solid rgba(0,0,0,0.3); border-radius: 50%;"></div>
      <div class="reg-mark top-right" style="position: absolute; top: 10px; right: 10px; width: 20px; height: 20px; border: 2px solid rgba(0,0,0,0.3); border-radius: 50%;"></div>
      <div class="reg-mark bottom-left" style="position: absolute; bottom: 10px; left: 10px; width: 20px; height: 20px; border: 2px solid rgba(0,0,0,0.3); border-radius: 50%;"></div>
      <div class="reg-mark bottom-right" style="position: absolute; bottom: 10px; right: 10px; width: 20px; height: 20px; border: 2px solid rgba(0,0,0,0.3); border-radius: 50%;"></div>
    </div>
    ${generateHudOverlay(item)}
  `;
}

function renderStencilSprayHtml(item, assetsBaseUrl) {
  const src = resolveDeckAssetUrl(item.img, assetsBaseUrl);
  return `
    <div style="position: absolute; inset: 0; background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);"></div>
    <div class="spray-sweep" aria-hidden="true"></div>
    <div class="stencil-outline">
      <img src="${src}" alt="${item.title}" style="width: 100%; height: 100%; object-fit: contain; filter: brightness(0.3) contrast(2);">
    </div>
    <div class="spray-fill">
      <img src="${src}" alt="${item.title}" style="width: 100%; height: 100%; object-fit: contain; filter: brightness(1.1) contrast(1.2) saturate(1.3);">
    </div>
    <canvas class="spray-overspray-canvas" data-kind="spray-overspray-canvas" width="800" height="600" style="position: absolute; inset: 0; pointer-events: none; mix-blend-mode: screen; opacity: 0.4;"></canvas>
    <div class="paint-drips"></div>
    ${generateHudOverlay(item)}
  `;
}

function renderEvidenceBoardHtml(item, assetsBaseUrl) {
  const src = resolveDeckAssetUrl(item.img, assetsBaseUrl);
  const evidenceId = getDeterministicEvidenceId(item);
  return `
    <div class="overscan">
      <div class="cork-board"></div>
      <div class="evidence-photo">
        <img src="${src}" alt="${item.title}">
      </div>
      <svg class="red-string" viewBox="0 0 100 100" preserveAspectRatio="none">
        <path d="M 10,10 Q 30,50 50,50 T 90,90" stroke="#ff0000" stroke-width="0.3" fill="none" opacity="0.6" />
        <path d="M 90,10 Q 70,30 50,50 T 10,90" stroke="#ff0000" stroke-width="0.3" fill="none" opacity="0.6" />
      </svg>
      <div class="evidence-label">EVIDENCE #${evidenceId}</div>
    </div>
    ${generateHudOverlay(item)}
  `;
}

function renderEvidenceScatterHtml(item, assetsBaseUrl) {
  const src = resolveDeckAssetUrl(item.img, assetsBaseUrl);
  return `
    <div class="overscan">
      <div class="evidence-table"></div>
      <div class="evidence-item item-1">
        <img src="${src}" alt="${item.title}">
        <div class="evidence-tag">01</div>
      </div>
      <div class="evidence-item item-2">
        <img src="${src}" alt="${item.title}">
        <div class="evidence-tag">02</div>
      </div>
      <div class="evidence-item item-3">
        <img src="${src}" alt="${item.title}">
        <div class="evidence-tag">03</div>
      </div>
      <div class="evidence-item item-4 primary">
        <img src="${src}" alt="${item.title}">
        <div class="evidence-tag">04</div>
      </div>
      <svg class="evidence-connections" viewBox="0 0 100 100" preserveAspectRatio="none">
        <path d="M 20,20 L 50,50" stroke="#ff0000" stroke-width="0.2" opacity="0.6" />
        <path d="M 80,30 L 50,50" stroke="#ff0000" stroke-width="0.2" opacity="0.6" />
        <path d="M 30,80 L 50,50" stroke="#ff0000" stroke-width="0.2" opacity="0.6" />
      </svg>
    </div>
    ${generateHudOverlay(item)}
  `;
}

function renderWarningExpansionHtml(item, assetsBaseUrl) {
  const src = resolveDeckAssetUrl(item.img, assetsBaseUrl);
  return `
    <div class="warning-block center"><img src="${src}" alt="${item.title}"></div>
    <div class="warning-block top" aria-hidden="true"><img src="${src}" alt=""></div>
    <div class="warning-block bottom" aria-hidden="true"><img src="${src}" alt=""></div>
    <div class="warning-block left" aria-hidden="true"><img src="${src}" alt=""></div>
    <div class="warning-block right" aria-hidden="true"><img src="${src}" alt=""></div>
    <div class="warning-border"></div>
    <div class="warning-label" style="position: absolute; top: 10px; left: 50%; transform: translateX(-50%); background: rgba(255,255,0,0.9); color: #000; padding: 10px 30px; border: 3px solid var(--safety-yellow); font-size: 1.8rem; font-weight: bold; z-index: 10;">⚠ WARNING ⚠</div>
    ${generateHudOverlay(item)}
  `;
}

function renderProjectorSputterHtml(item, assetsBaseUrl) {
  const src = resolveDeckAssetUrl(item.img, assetsBaseUrl);
  return `
    <div class="projector-content">
      <img src="${src}" alt="${item.title}">
    </div>
    <div class="film-grain"></div>
    ${generateHudOverlay(item)}
  `;
}

function renderTeletypeAssembleHtml(item, assetsBaseUrl) {
  const src = resolveDeckAssetUrl(item.img, assetsBaseUrl);
  return `
    <div style="position: absolute; inset: 0; background: linear-gradient(180deg, #f5f5dc 0%, #e8e8d0 100%);"></div>
    <div class="teletype-paper">
      <img src="${src}" alt="${item.title}">
      <div style="position: absolute; inset: 0; background: repeating-linear-gradient(0deg, transparent 0px, transparent 24px, rgba(0,0,0,0.03) 24px, rgba(0,0,0,0.03) 25px); pointer-events: none;"></div>
    </div>
    <div class="teletype-carriage"></div>
    <canvas class="teletype-canvas" data-kind="teletype-canvas" width="800" height="600" aria-hidden="true"></canvas>
    ${generateHudOverlay(item)}
  `;
}

function renderComparisonSplitHtml(item, assetsBaseUrl) {
  const src = resolveDeckAssetUrl(item.img, assetsBaseUrl);
  return `
    <div class="split-half left-half" style="position: absolute; left: 0; top: 0; width: 50%; height: 100%; overflow: hidden;">
      <img src="${src}" alt="${item.title}" style="width: 200%; height: 100%; object-fit: cover; filter: hue-rotate(180deg) brightness(0.8);">
    </div>
    <div class="split-half right-half" style="position: absolute; right: 0; top: 0; width: 50%; height: 100%; overflow: hidden;">
      <img src="${src}" alt="${item.title}" style="position: absolute; right: 0; width: 200%; height: 100%; object-fit: cover; filter: brightness(0.9);">
    </div>
    <div class="split-divider" style="position: absolute; left: 50%; top: 0; bottom: 0; width: 4px; background: var(--safety-yellow); transform: translateX(-50%); z-index: 5;">
      <div class="vs-symbol" style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); background: rgba(0,0,0,0.9); color: var(--safety-yellow); padding: 15px 25px; border: 3px solid var(--safety-yellow); font-size: 1.8rem; font-weight: bold; z-index: 10; animation: pulse 2s ease-in-out infinite;">VS</div>
      <canvas class="spark-canvas" data-kind="spark-canvas" width="100" height="600" style="position: absolute; left: 50%; top: 0; transform: translateX(-50%); pointer-events: none; mix-blend-mode: screen;"></canvas>
    </div>
    ${generateHudOverlay(item)}
  `;
}

function renderQuantumHtml(item, assetsBaseUrl) {
  return `
    <img src="${resolveDeckAssetUrl(item.img, assetsBaseUrl)}" alt="${item.title}" style="filter: brightness(0.7) blur(1px);">
    <canvas class="quantum-canvas" data-kind="quantum-canvas" width="800" height="600" style="position: absolute; inset: 0; mix-blend-mode: screen; opacity: 0.8;"></canvas>
    <div style="position: absolute; inset: 0; background: radial-gradient(circle at 50% 50%, transparent 0%, rgba(0,0,0,0.5) 100%); pointer-events: none;"></div>
    ${generateHudOverlay(item)}
  `;
}

function renderImmersiveHtml(item, assetsBaseUrl) {
  const src = resolveDeckAssetUrl(item.img, assetsBaseUrl);
  return `
    <div style="position: absolute; inset: 0; overflow: hidden;">
      <img src="${src}" alt="${item.title}" style="position: absolute; inset: -10%; width: 120%; height: 120%; object-fit: cover; filter: brightness(0.6) blur(8px); transform: scale(1.1);">
      ${renderBackgroundMediaHtml({
        imgSrc: src,
        alt: item.title,
        style: 'position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; filter: brightness(0.85) contrast(1.2); z-index: 2;'
      })}
      <div style="position: absolute; inset: 0; background: radial-gradient(circle at 50% 50%, transparent 0%, rgba(0,0,0,0.4) 100%); pointer-events: none;"></div>
    </div>
    ${generateHudOverlay(item)}
  `;
}

function renderLiquidMetalHtml(item, assetsBaseUrl) {
  const src = resolveDeckAssetUrl(item.img, assetsBaseUrl);
  return `
    <img src="${src}" alt="${item.title}">
  `;
}

function renderPrismHtml(item, assetsBaseUrl) {
  const src = resolveDeckAssetUrl(item.img, assetsBaseUrl);
  return `
    <img src="${src}" alt="${item.title}" class="prism-base">
    <div class="prism-layer prism-layer-a" aria-hidden="true"></div>
    <div class="prism-layer prism-layer-b" aria-hidden="true"></div>
    <div class="prism-rays" aria-hidden="true"></div>
    ${generateHudOverlay(item)}
  `;
}

function renderDataDashboardHtml(item, assetsBaseUrl) {
  return `
    <img src="${resolveDeckAssetUrl(item.img, assetsBaseUrl)}" alt="${item.title}">
    ${generateHudOverlay(item)}
  `;
}

export const slidesTemplateRenderers2029 = {
  title: renderGlobalTitleHtml,
  'sector-landing': renderSectorLandingHtml,
  'sector-model': renderSectorModelHtml,
  'sector-cta': renderSectorCtaHtml,
  'data-dashboard': renderDataDashboardHtml,
  immersive: renderImmersiveHtml,
  comparison: renderComparisonHtml,
  timeline: renderTimelineHtml,
  'factory-stamp': renderFactoryStampHtml,
  'propaganda-drop': renderPropagandaDropHtml,
  'broadcast-interrupt': renderBroadcastInterruptHtml,
  'wheat-paste': renderWheatPasteHtml,
  'print-press': renderPrintPressHtml,
  'stencil-spray': renderStencilSprayHtml,
  'evidence-board': renderEvidenceBoardHtml,
  'evidence-scatter': renderEvidenceScatterHtml,
  'warning-expansion': renderWarningExpansionHtml,
  'projector-sputter': renderProjectorSputterHtml,
  'teletype-assemble': renderTeletypeAssembleHtml,
  'comparison-split': renderComparisonSplitHtml,
  'full-bleed': renderFullBleedHtml,
  grid: renderGridHtml,
  terminal: renderTerminalHtml,
  'glitch-art': renderGlitchArtHtml,
  hologram: renderHologramHtml,
  'matrix-rain': renderMatrixRainHtml,
  explosive: renderExplosiveHtml,
  kaleidoscope: renderKaleidoscopeHtml,
  vortex: renderVortexHtml,
  'neon-city': renderNeonCityHtml,
  quantum: renderQuantumHtml,
  prism: renderPrismHtml,
  'liquid-metal': renderLiquidMetalHtml,
  cosmic: renderCosmicHtml,
  shockwave: renderShockwaveHtml
};

export const fullBleedTemplateNames2029 = new Set(['title', 'sector-landing', 'sector-model', 'sector-cta']);

export function renderSlideTemplateHtml2029({ templateName, item, assetsBaseUrl }) {
  const resolvedName = templateName ? String(templateName) : '';
  const fn = slidesTemplateRenderers2029[resolvedName] || slidesTemplateRenderers2029.immersive;
  if (!fn) return '';
  return fn(item, assetsBaseUrl);
}

export const slidesTemplatePack2029 = {
  renderSlideTemplateHtml: renderSlideTemplateHtml2029,
  fullBleedTemplateNames: fullBleedTemplateNames2029,
  templateNames: Object.keys(slidesTemplateRenderers2029),
  resolveDeckAssetUrl
};
